import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchStrategyManagement, type ManagedStrategy, type StrategyManagementData } from './api.js';

/** @param value - Numeric dollars. @returns Compact US-dollar label. */
function dollars(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** @param value - Scale-100 percentage. @returns Concise percentage label. */
function percent100(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * Selects the enabled or newest visible version used for family-level detail.
 * @param strategies - Strategy versions sharing a stable key.
 * @returns Preferred strategy version, if one exists.
 */
export function selectDisplayedStrategy(strategies: readonly ManagedStrategy[]): ManagedStrategy | undefined {
  return strategies.find((strategy) => strategy.enabled) ?? strategies[0];
}

/** Generic detail page for one database-backed strategy family. */
export function StrategyDetail(props: { readonly strategyKey: string }): ReactNode {
  const [data, setData] = useState<StrategyManagementData | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** @returns Nothing after refreshing the strategy snapshot. */
  const refresh = useCallback(async (): Promise<void> => {
    try {
      setData(await fetchStrategyManagement());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const poll = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(poll);
  }, [refresh]);

  const versions = useMemo(
    () => data?.strategies.filter((strategy) => strategy.key === props.strategyKey) ?? [],
    [data, props.strategyKey],
  );
  const strategy = selectDisplayedStrategy(versions);

  useEffect(() => {
    document.title = `${strategy?.name ?? 'Strategy'} · PredictionTrader`;
  }, [strategy?.name]);

  if (error !== null) {
    return <div className="strategy-page"><a className="back-link" href="/">← Overview</a><div className="alert"><b>Strategy unavailable</b><span>{error}</span></div></div>;
  }
  if (data === null) return <div className="empty detail-loading">Loading strategy detail…</div>;
  if (strategy === undefined) {
    return <div className="strategy-page"><a className="back-link" href="/">← Overview</a><section className="panel not-found"><h1>Strategy not found</h1><p>No strategy uses the key <code>{props.strategyKey}</code>.</p></section></div>;
  }

  return (
    <div className="strategy-page">
      <a className="back-link" href="/">← Overview</a>
      <header className="hero strategy-hero detail-hero">
        <div><p className="eyebrow">Paper strategy</p><h1>{strategy.name}</h1><p>{strategy.description}</p></div>
        <div className="hero__meta"><span>Route</span><strong>/strategy/{strategy.key}</strong></div>
      </header>

      <section className="metric-grid strategy-metrics" aria-label="Strategy summary">
        <div className="metric metric--good"><span>Allocation</span><strong>{percent100(strategy.allocationPercent100)}</strong></div>
        <div className="metric"><span>Allocated capital</span><strong>{dollars(strategy.allocatedCapitalDollars)}</strong></div>
        <div className="metric"><span>Available capital</span><strong>{dollars(strategy.availableCapitalDollars)}</strong></div>
        <div className="metric"><span>Realized P&amp;L</span><strong className={strategy.realizedPnlDollars >= 0 ? 'positive' : 'negative'}>{dollars(strategy.realizedPnlDollars)}</strong></div>
        <div className="metric"><span>Open positions</span><strong>{strategy.openPositions}</strong></div>
        <div className="metric"><span>Status</span><strong>{strategy.enabled ? 'Enabled' : 'Disabled'}</strong></div>
      </section>

      <div className="strategy-detail-grid">
        <section className="panel">
          <header className="panel__header"><h2>Performance &amp; evidence</h2><span className="count">paper observations</span></header>
          <div className="detail-stat-grid">
            <div><span>Return</span><b className={strategy.returnPercent100 >= 0 ? 'positive' : 'negative'}>{percent100(strategy.returnPercent100)}</b></div>
            <div><span>Signals</span><b>{strategy.signals}</b></div>
            <div><span>Plans</span><b>{strategy.plans}</b></div>
            <div><span>Hypothetical orders</span><b>{strategy.hypotheticalOrders}</b></div>
            <div><span>Fills</span><b>{strategy.fills}</b></div>
            <div><span>Unique markets</span><b>{strategy.uniqueMarkets}</b></div>
            <div><span>Market-days</span><b>{strategy.uniqueMarketDays}</b></div>
            <div><span>Locked capital</span><b>{dollars(strategy.lockedCapitalDollars)}</b></div>
          </div>
        </section>

        <section className="panel">
          <header className="panel__header"><h2>Strategy identity</h2><span className={`tag ${strategy.enabled ? 'tag--good' : 'tag--neutral'}`}>{strategy.enabled ? 'enabled' : 'disabled'}</span></header>
          <div className="health-list">
            <div><span>Key</span><b>{strategy.key}</b></div>
            <div><span>Version</span><b>{strategy.version}</b></div>
            <div><span>Family</span><b>{strategy.family.replaceAll('_', ' ').toLowerCase()}</b></div>
            <div><span>Venue</span><b>{strategy.venue.replaceAll('_', ' ').toLowerCase()}</b></div>
            <div><span>Mode</span><b>{strategy.mode.toLowerCase()}</b></div>
            <div><span>Known versions</span><b>{versions.length}</b></div>
          </div>
        </section>
      </div>

      <details className="panel disclosure-panel">
        <summary><span>Configuration</span><small>{strategy.version} · immutable</small></summary>
        <pre>{JSON.stringify(strategy.config, null, 2)}</pre>
      </details>

      <p className="paper-safety">This page is specific to <b>{strategy.name}</b>. Allocation changes and version management remain on the <a href="/">Overview</a>.</p>
    </div>
  );
}
